'use client'
import { useEffect, useId, useRef, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { AnimatePresence, motion } from 'framer-motion'
import { useLanguage } from '@/lib/context/LanguageContext'
import { useScrolled } from '@/lib/hooks/useScrolled'
import CartButton from '@/components/cart/CartButton'

/**
 * Top navigation. The hero is warm ivory, so the bar keeps dark ink text and
 * a dark logo at all times — no light/dark inversion. Two states:
 *   - At the top of the page: transparent, with the monogram + CTA at their
 *     larger "hero" size.
 *   - After scrolling past the threshold: a backdrop-blurred surface +
 *     hairline border fades in *behind* the already-dark text, and the
 *     monogram + CTA spring down to their compact size — same behaviour as
 *     the Trend navbar.
 *
 * Colours never flip; only the background/blur/border fade and the logo+CTA
 * scale. The bar height is pinned to 96px (= globals' scroll-padding-top, which
 * Lenis honours on anchor jumps) so the resizing elements don't make the bar jump.
 *
 * Includes a screen-reader-only skip-link that becomes visible on
 * keyboard focus, targeting #main.
 */
// Spring shared by the logo + CTA resize — matches Trend's feel.
const RESIZE_SPRING = { type: 'spring', stiffness: 260, damping: 30, mass: 0.8 } as const

// Links shown in the menus. `kind` lets the mobile sheet render section
// anchors and the /journal route with the right element. The explicit type
// keeps the route/anchor union alive while the TEMP entries below are
// commented out (an all-anchor `as const` would narrow `kind` and break the
// `l.kind === 'route'` branch in the mobile sheet).
type NavLink = {
  kind: 'anchor' | 'route'
  href: string
  key: 'products' | 'why' | 'journal'
}
const NAV_LINKS: readonly NavLink[] = [
  // TEMP (revert this week): «محصولات» anchor dropped — /products («فروشگاه»)
  // is the single shopping entry; the homepage section + footer anchor remain.
  // { kind: 'anchor', href: '#products', key: 'products' },
  { kind: 'anchor', href: '#why', key: 'why' },
  // TEMP (revert this week): markets / map section disabled
  // { kind: 'anchor', href: '#markets', key: 'markets' },
  // TEMP (revert this week): FAQ section disabled
  // { kind: 'anchor', href: '#faq', key: 'faq' },
  // TEMP (revert this week): journal hidden while it has no published posts
  // { kind: 'route', href: '/journal', key: 'journal' },
]

export default function Navbar() {
  const { t } = useLanguage()
  // Shrink a little later than the blur kicks in, so the hero gets a beat.
  const scrolled = useScrolled(48)

  // Mobile menu (< md). Hidden behind a hamburger so small screens can still
  // reach every section + the Journal route, which previously only the footer
  // exposed below md.
  const [open, setOpen] = useState(false)
  const menuId = useId()
  const toggleRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)

  // Esc closes and returns focus to the toggle; focus moves into the panel on
  // open. The bar leaves its transparent state while the menu is open so the
  // links stay legible over the hero.
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setOpen(false)
        toggleRef.current?.focus()
      }
    }
    document.addEventListener('keydown', onKey)
    panelRef.current?.querySelector<HTMLElement>('a, button')?.focus()
    return () => document.removeEventListener('keydown', onKey)
  }, [open])

  const solid = scrolled || open

  // The hero section anchors (#top, #products, #why, #contact …) only exist on
  // the homepage. From any other route (e.g. /checkout) point them at the
  // homepage so they navigate home and then scroll, instead of doing nothing.
  const pathname = usePathname()
  const anchor = (hash: string) => (pathname === '/' ? hash : `/${hash}`)

  return (
    <header
      className={
        'fixed inset-x-0 top-0 z-30 transition-[background-color,backdrop-filter,border-color] duration-300 ease-out ' +
        (solid
          ? 'bg-surface/85 backdrop-blur-lg border-b border-ink/[0.06]'
          : 'bg-transparent border-b border-transparent')
      }
    >
      <a
        href="#main"
        className="sr-only focus:not-sr-only focus:absolute focus:left-5 focus:top-5 focus:z-30 focus:bg-surface focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-ink focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-ink"
      >
        Skip to content
      </a>
      {/* 3-col grid (1fr · auto · 1fr) so the nav links sit on the TRUE centre
          axis — aligned with the centred hero — instead of being pushed off by
          justify-between, which equalises the side gaps and lets the wider
          actions cluster shove the middle group toward the logo. */}
      <div className="mx-auto max-w-7xl px-5 sm:px-8 h-24 grid grid-cols-[1fr_auto_1fr] items-center gap-8">
        <motion.a
          href={anchor('#top')}
          aria-label="MRN — Mah Rokh Naz — back to top"
          className="inline-flex items-center justify-self-start will-change-transform"
          style={{ transformOrigin: 'left center' }}
          initial={false}
          animate={{ scale: scrolled ? 0.8 : 1 }}
          transition={RESIZE_SPRING}
        >
          {/* Full master lockup (monogram + red ring + subtitle), on-light. */}
          <img
            src="/brand/mrn-logo-full.svg"
            alt="MRN — Mah Rokh Naz"
            className="h-16 w-auto"
            width={531}
            height={407}
          />
        </motion.a>

        <nav className="hidden md:flex items-center justify-self-center gap-10 text-base font-medium text-ink/80">
          {/* TEMP (revert this week): «محصولات» anchor dropped — «فروشگاه» is
              the single shopping entry (homepage section + footer anchor remain)
          <a href={anchor('#products')} className="focus-ring hover:text-ink transition-colors">
            {t.nav.products}
          </a> */}
          {/* TEMP (revert this week): markets / map section disabled
          <a href="#markets" className="focus-ring hover:text-ink transition-colors">
            {t.nav.markets ?? 'Markets'}
          </a> */}
          <Link href="/products" className="focus-ring hover:text-ink transition-colors">
            {t.shop.title}
          </Link>
          {/* TEMP (revert this week): journal hidden while it has no published posts
          <Link href="/journal" className="focus-ring hover:text-ink transition-colors">
            {t.nav.journal ?? 'Journal'}
          </Link> */}
        </nav>

        <div className="flex items-center justify-self-end gap-1.5">
          <Link
            href="/account"
            aria-label={t.nav.account}
            className="focus-ring hidden h-11 w-11 items-center justify-center text-ink hover:text-accent transition-colors sm:inline-flex"
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
              <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
          </Link>
          <CartButton />
          <motion.a
            href={anchor('#contact')}
            className="hidden sm:inline-flex items-center gap-2 rounded-none font-semibold bg-action text-surface hover:bg-accent transition-colors will-change-transform focus-ring"
            initial={false}
            animate={{ scale: scrolled ? 15 / 17 : 1 }}
            transition={RESIZE_SPRING}
            style={{ fontSize: 17, paddingInline: '1.5em', paddingBlock: '0.78em', transformOrigin: 'right center' }}
          >
            {t.nav.cta}
          </motion.a>

          {/* Hamburger — small screens only. */}
          <button
            ref={toggleRef}
            type="button"
            className="focus-ring md:hidden -mr-1 inline-flex h-11 w-11 items-center justify-center text-ink"
            aria-label={open ? (t.nav.menuClose ?? 'Close menu') : (t.nav.menu ?? 'Menu')}
            aria-expanded={open}
            aria-controls={menuId}
            onClick={() => setOpen((v) => !v)}
          >
            <span aria-hidden className="relative block h-4 w-6">
              <span
                className={
                  'absolute left-0 block h-0.5 w-6 bg-current transition-transform duration-300 ' +
                  (open ? 'top-1/2 -translate-y-1/2 rotate-45' : 'top-0')
                }
              />
              <span
                className={
                  'absolute left-0 top-1/2 block h-0.5 w-6 -translate-y-1/2 bg-current transition-opacity duration-200 ' +
                  (open ? 'opacity-0' : 'opacity-100')
                }
              />
              <span
                className={
                  'absolute left-0 block h-0.5 w-6 bg-current transition-transform duration-300 ' +
                  (open ? 'top-1/2 -translate-y-1/2 -rotate-45' : 'bottom-0')
                }
              />
            </span>
          </button>
        </div>
      </div>

      {/* ── Mobile menu ─────────────────────────────────────────────────── */}
      <AnimatePresence>
        {open && (
          <>
            {/* Click-away scrim below the bar. */}
            <motion.div
              className="md:hidden fixed inset-x-0 bottom-0 top-24 z-20 bg-ink/20"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={() => setOpen(false)}
              aria-hidden
            />
            <motion.div
              ref={panelRef}
              id={menuId}
              className="md:hidden absolute inset-x-0 top-24 z-20 border-b border-ink/[0.06] bg-surface/95 backdrop-blur-lg"
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -8 }}
              transition={{ duration: 0.22, ease: [0.21, 0.47, 0.32, 0.98] }}
            >
              <nav className="mx-auto flex max-w-7xl flex-col px-5 py-3 sm:px-8">
                {NAV_LINKS.map((l) =>
                  l.kind === 'route' ? (
                    <Link
                      key={l.key}
                      href={l.href}
                      onClick={() => setOpen(false)}
                      className="focus-ring flex min-h-[44px] items-center text-base font-medium text-ink/80 hover:text-ink transition-colors"
                    >
                      {t.nav[l.key] ?? l.key}
                    </Link>
                  ) : (
                    <a
                      key={l.key}
                      href={anchor(l.href)}
                      onClick={() => setOpen(false)}
                      className="focus-ring flex min-h-[44px] items-center text-base font-medium text-ink/80 hover:text-ink transition-colors"
                    >
                      {t.nav[l.key] ?? l.key}
                    </a>
                  ),
                )}
                <Link
                  href="/products"
                  onClick={() => setOpen(false)}
                  className="focus-ring flex min-h-[44px] items-center text-base font-medium text-ink/80 hover:text-ink transition-colors"
                >
                  {t.shop.title}
                </Link>
                <Link
                  href="/account"
                  onClick={() => setOpen(false)}
                  className="focus-ring flex min-h-[44px] items-center text-base font-medium text-ink/80 hover:text-ink transition-colors"
                >
                  {t.nav.account}
                </Link>
                <a
                  href={anchor('#contact')}
                  onClick={() => setOpen(false)}
                  className="focus-ring mt-2 mb-2 inline-flex min-h-[44px] items-center justify-center gap-2 bg-action px-6 font-semibold text-surface hover:bg-accent transition-colors"
                >
                  {t.nav.cta}
                </a>
              </nav>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  )
}
